import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCaseService, type UseCase, type ListRecordsResponse } from '../services/useCase.service';

export function useUseCases(options?: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  enabled?: boolean;
}) {
  const {
    page = 1,
    pageSize = 50,
    filters = {},
    enabled = true,
  } = options || {};

  return useQuery<ListRecordsResponse<UseCase>>({
    queryKey: ['useCases', page, pageSize, filters],
    queryFn: () => useCaseService.find({ page, pageSize, filters }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUseCase(id: string | null | undefined) {
  return useQuery<UseCase>({
    queryKey: ['useCase', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('UseCase ID is required'));
      return useCaseService.findById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateUseCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (useCase: Partial<UseCase>) => useCaseService.create(useCase),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['useCases'] });
    },
  });
}

export function useUpdateUseCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (useCase: Partial<UseCase>) => useCaseService.save(useCase),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['useCases'] });
      queryClient.invalidateQueries({ queryKey: ['useCase', data.id] });
    },
  });
}

export function useDeleteUseCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => useCaseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['useCases'] });
    },
  });
}
